import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = "";

export default function WalletSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  
  const [settings, setSettings] = useState({
    isEnabled: true,
    minAmount: 100,
    maxAmount: 1000000,
    cardPaymentEnabled: true,
    virtualAccountEnabled: true,
    dailyLimit: 5000000
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    };
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/wallet/settings`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          setSettings(data.data);
        }
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    if (typeof value === 'string' && field !== 'isEnabled' && field !== 'cardPaymentEnabled' && field !== 'virtualAccountEnabled') {
      const numValue = parseInt(value.replace(/,/g, ""), 10);
      setSettings(prev => ({
        ...prev,
        [field]: isNaN(numValue) ? 0 : numValue
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/wallet/settings`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error("Failed to save settings");
      }

      setSuccess("Wallet settings saved successfully!");
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setError("Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading settings...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .toggle-switch { position: relative; width: 50px; height: 26px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .toggle-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: 0.3s; border-radius: 26px; }
        .toggle-slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 3px; bottom: 3px; background-color: white; transition: 0.3s; border-radius: 50%; }
        input:checked + .toggle-slider { background-color: #10b981; }
        input:checked + .toggle-slider:before { transform: translateX(24px); }
        .input-field:focus { border-color: #1183FB; outline: none; box-shadow: 0 0 0 3px rgba(17, 131, 251, 0.2); }
      `}</style>

      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => navigate("/admin")} style={styles.backBtn}>←</button>
          <div>
            <h1 style={styles.headerTitle}>Wallet Settings</h1>
            <p style={styles.headerSubtitle}>Configure wallet funding options</p>
          </div>
        </div>
        <button onClick={handleSave} style={styles.saveBtn} disabled={saving}>
          {saving ? "Saving..." : "💾 Save Settings"}
        </button>
      </header>

      <main style={styles.main}>
        {/* Alerts */}
        {success && <div style={styles.successAlert}>✅ {success}</div>}
        {error && <div style={styles.errorAlert}>❌ {error}</div>}

        {/* Master Toggle */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h3 style={styles.cardTitle}>🔌 Wallet Funding</h3>
              <p style={styles.cardSubtitle}>Enable or disable wallet funding for all users</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.isEnabled}
                onChange={(e) => handleChange("isEnabled", e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Funding Methods */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>💳 Funding Methods</h3>
          
          <div style={styles.toggleRow}>
            <div style={styles.toggleInfo}>
              <span style={styles.toggleLabel}>Card Payment</span>
              <span style={styles.toggleDesc}>Allow users to fund via debit/credit card</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.cardPaymentEnabled}
                onChange={(e) => handleChange("cardPaymentEnabled", e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div style={styles.toggleRow}>
            <div style={styles.toggleInfo}>
              <span style={styles.toggleLabel}>Virtual Account (Bank Transfer)</span>
              <span style={styles.toggleDesc}>Allow users to create dedicated virtual accounts</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.virtualAccountEnabled}
                onChange={(e) => handleChange("virtualAccountEnabled", e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>

        {/* Amount Limits */}
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>💰 Funding Limits</h3>
          
          <div style={styles.fieldsGrid}>
            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Minimum Amount</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputPrefix}>₦</span>
                <input
                  type="text"
                  className="input-field"
                  style={styles.input}
                  value={settings.minAmount.toLocaleString()}
                  onChange={(e) => handleChange("minAmount", e.target.value)}
                />
              </div>
              <span style={styles.fieldHint}>Minimum amount users can fund</span>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Maximum Amount</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputPrefix}>₦</span>
                <input
                  type="text"
                  className="input-field"
                  style={styles.input}
                  value={settings.maxAmount.toLocaleString()}
                  onChange={(e) => handleChange("maxAmount", e.target.value)}
                />
              </div>
              <span style={styles.fieldHint}>Maximum amount per funding transaction</span>
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.fieldLabel}>Daily Limit (Per User)</label>
              <div style={styles.inputWrapper}>
                <span style={styles.inputPrefix}>₦</span>
                <input
                  type="text"
                  className="input-field"
                  style={styles.input}
                  value={settings.dailyLimit.toLocaleString()}
                  onChange={(e) => handleChange("dailyLimit", e.target.value)}
                />
              </div>
              <span style={styles.fieldHint}>Maximum total funding per user per day</span>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div style={styles.summaryCard}>
          <h4 style={styles.summaryTitle}>📊 Current Settings Summary</h4>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Status</span>
              <span style={{
                ...styles.summaryValue,
                color: settings.isEnabled ? '#10b981' : '#ef4444'
              }}>
                {settings.isEnabled ? '✅ Enabled' : '❌ Disabled'}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Card Payment</span>
              <span style={styles.summaryValue}>
                {settings.cardPaymentEnabled ? '✓ Yes' : '✗ No'}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Virtual Account</span>
              <span style={styles.summaryValue}>
                {settings.virtualAccountEnabled ? '✓ Yes' : '✗ No'}
              </span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Min Amount</span>
              <span style={styles.summaryValue}>{formatCurrency(settings.minAmount)}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Max Amount</span>
              <span style={styles.summaryValue}>{formatCurrency(settings.maxAmount)}</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Daily Limit</span>
              <span style={styles.summaryValue}>{formatCurrency(settings.dailyLimit)}</span>
            </div>
          </div>
        </div>

        {/* Paystack Info */}
        <div style={styles.infoCard}>
          <h4 style={styles.infoTitle}>ℹ️ Paystack Integration</h4>
          <p style={styles.infoText}>
            Wallet funding is powered by Paystack. Ensure your Paystack credentials are 
            configured in the backend settings. Webhook URL should be set to:
          </p>
          <code style={styles.codeBlock}>
            {window.location.origin}/api/webhooks/paystack
          </code>
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", background: "#f1f5f9", fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" },
  loadingContainer: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", gap: "16px" },
  spinner: { width: "48px", height: "48px", border: "4px solid #e2e8f0", borderTopColor: "#1183FB", borderRadius: "50%", animation: "spin 1s linear infinite" },

  header: { background: "linear-gradient(135deg, #011745, #1183FB)", padding: "20px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "16px" },
  backBtn: { background: "rgba(255,255,255,0.2)", border: "none", borderRadius: "12px", color: "white", width: "44px", height: "44px", fontSize: "20px", cursor: "pointer" },
  headerTitle: { color: "white", fontSize: "24px", fontWeight: "700", margin: 0 },
  headerSubtitle: { color: "rgba(255,255,255,0.8)", fontSize: "14px", margin: "4px 0 0 0" },
  saveBtn: { background: "#10b981", border: "none", borderRadius: "12px", color: "white", padding: "14px 28px", fontSize: "15px", fontWeight: "600", cursor: "pointer" },

  main: { padding: "24px 32px", maxWidth: "800px", margin: "0 auto" },

  successAlert: { background: "#d1fae5", color: "#065f46", padding: "16px 20px", borderRadius: "12px", marginBottom: "20px", fontWeight: "500" },
  errorAlert: { background: "#fee2e2", color: "#991b1b", padding: "16px 20px", borderRadius: "12px", marginBottom: "20px", fontWeight: "500" },

  card: { background: "white", borderRadius: "16px", padding: "24px", marginBottom: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" },
  cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  cardTitle: { fontSize: "18px", fontWeight: "700", color: "#011745", margin: 0 },
  cardSubtitle: { fontSize: "14px", color: "#64748b", margin: "4px 0 0 0" },

  sectionTitle: { fontSize: "16px", fontWeight: "700", color: "#011745", marginTop: 0, marginBottom: "20px" },

  toggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 0", borderBottom: "1px solid #f1f5f9" },
  toggleInfo: { display: "flex", flexDirection: "column", gap: "4px" },
  toggleLabel: { fontSize: "15px", fontWeight: "600", color: "#374151" },
  toggleDesc: { fontSize: "13px", color: "#94a3b8" },

  fieldsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" },
  fieldGroup: { display: "flex", flexDirection: "column", gap: "8px" },
  fieldLabel: { fontSize: "14px", fontWeight: "600", color: "#374151" },
  inputWrapper: { position: "relative", display: "flex", alignItems: "center" },
  inputPrefix: { position: "absolute", left: "14px", fontSize: "16px", fontWeight: "600", color: "#64748b" },
  input: { width: "100%", padding: "14px 14px 14px 36px", border: "2px solid #e2e8f0", borderRadius: "10px", fontSize: "16px", fontWeight: "600", color: "#011745", transition: "all 0.2s" },
  fieldHint: { fontSize: "12px", color: "#94a3b8" },

  summaryCard: { background: "linear-gradient(135deg, #f8fafc, #f1f5f9)", borderRadius: "16px", padding: "20px", marginBottom: "20px" },
  summaryTitle: { fontSize: "16px", fontWeight: "700", color: "#011745", marginTop: 0, marginBottom: "16px" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "16px" },
  summaryItem: { display: "flex", flexDirection: "column", gap: "4px" },
  summaryLabel: { fontSize: "12px", color: "#64748b", fontWeight: "600" },
  summaryValue: { fontSize: "15px", fontWeight: "700", color: "#011745" },

  infoCard: { background: "#fef3c7", borderRadius: "16px", padding: "20px" },
  infoTitle: { fontSize: "15px", fontWeight: "600", color: "#92400e", marginTop: 0, marginBottom: "12px" },
  infoText: { fontSize: "14px", color: "#b45309", margin: "0 0 12px 0", lineHeight: "1.5" },
  codeBlock: { display: "block", background: "#fde68a", borderRadius: "8px", padding: "12px", fontSize: "13px", color: "#78350f", wordBreak: "break-all" }
};
